import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../store';
import { loginStart, loginSuccess, loginFailure, logout, updateUser as updateUserAction } from '../store/slices/authSlice';
import { login as loginService, registerAgency as registerAgencyService, getProfile as getProfileService, deleteAccount as deleteAccountService } from '../services/authService';
import { isTwoFactorChallenge } from '../services/twoFactorService';
import { StorageManager } from '../utils/storageManager';
import { NON_AGENCY_SINGLE_SEGMENT_ROUTES } from '../constants/seoRoutes';

export const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading, error, agency } = useSelector((state: RootState) => state.auth);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    const checkAuth = async () => {
      // Prevent multiple auth checks
      if (hasCheckedAuth.current) {
        return;
      }
      
      const storedToken = StorageManager.getToken();
      console.log('useAuth checkAuth - storedToken:', storedToken ? 'Present' : 'Missing');
      console.log('useAuth checkAuth - current user:', user);
      console.log('useAuth checkAuth - current path:', window.location.pathname);
      console.log('useAuth checkAuth - Storage test result:', StorageManager.testStorage());
      
      if (storedToken && !user) {
        console.log('useAuth checkAuth - Token found, user not loaded, attempting to get profile');
        dispatch(loginStart());
        try {
          const isAdmin = window.location.pathname.startsWith('/admin');
          console.log('useAuth checkAuth - isAdmin:', isAdmin);
          
          const response = await getProfileService(isAdmin);
          console.log('useAuth checkAuth - profile response:', response);
          
          if (response.user) {
            console.log('useAuth checkAuth - User found, dispatching loginSuccess');
            dispatch(loginSuccess({ user: response.user, token: storedToken, agency: response.agency }));
            
            // Only redirect if we're on an unauthenticated page
            const currentPath = window.location.pathname;
            const isOnLoginPage = currentPath === '/login' || currentPath === '/individual/login' || currentPath === '/admin/login';
            const isOnRootPage = currentPath === '/';
            const isOnPasswordPage = currentPath === '/set-password' || currentPath === '/reset-password' || currentPath.includes('/forgot-password');
            const pathParts = currentPath.split('/').filter(Boolean);
            const isSingleSegment = pathParts.length === 1;
            const isOnAgencyLoginPage = isSingleSegment && !NON_AGENCY_SINGLE_SEGMENT_ROUTES.has(pathParts[0]);
            
            if (isOnLoginPage || isOnRootPage || isOnAgencyLoginPage) {
              if (isAdmin) {
                navigate('/admin/dashboard');
              } else if (response.agency?.slug) {
                navigate(`/agencies/${response.agency.slug}/dashboard`);
              } else {
                navigate('/dashboard');
              }
            }
            // If we're on a valid authenticated page (like property details), don't redirect
          } else {
            console.log('useAuth checkAuth - No user in response, logging out');
            dispatch(logout());
            navigate(isAdmin ? '/admin/login' : '/login');
          }
        } catch (error) {
          console.error('useAuth checkAuth - Error getting profile:', error);
          dispatch(logout());
          navigate(window.location.pathname.startsWith('/admin') ? '/admin/login' : '/login');
        }
      } else if (!storedToken) {
        console.log('useAuth checkAuth - No token found');
        const currentPath = window.location.pathname;
        const isAdmin = currentPath.startsWith('/admin');
        const isOnContractorPage = currentPath.startsWith('/contractor') || currentPath.startsWith('/jobs');
        const isOnLoginPage = currentPath === '/login' || currentPath === '/individual/login' || currentPath === '/admin/login' || currentPath === '/register' || currentPath === '/';
        const isOnPasswordPage = currentPath === '/set-password' || currentPath === '/reset-password' || currentPath.includes('/forgot-password');
        
        // Check if we're on an agency-specific login page (e.g., /tracy-yap-realty)
        // Agency login pages are routes that don't start with /agencies/ and aren't protected routes
        const pathParts = currentPath.split('/').filter(Boolean);
        const isSingleSegment = pathParts.length === 1;
        const isOnAgencyLoginPage = isSingleSegment && !NON_AGENCY_SINGLE_SEGMENT_ROUTES.has(pathParts[0]);
        
        // Only redirect if we're not already on a login page, password reset page, or contractor page
        if (!isOnLoginPage && !isOnAgencyLoginPage && !isOnPasswordPage && !isOnContractorPage) {
          console.log('useAuth checkAuth - Not on login page, redirecting');
          navigate(isAdmin ? '/admin/login' : '/login');
        }
      }
      
      hasCheckedAuth.current = true;
    };

    checkAuth();
  }, [dispatch, navigate]); // Removed 'user' from dependencies to prevent infinite loop

  // Handle navigation based on auth state
  useEffect(() => {
    if (user) {
      if (user.primaryRole === 'app_admin') {
        const currentPath = window.location.pathname;
        if (currentPath === '/admin/login') {
          navigate('/admin/dashboard');
        }
      }
    }
  }, [user, navigate]);

  const login = async (email: string, password: string, isAdmin: boolean = false) => {
    console.log('useAuth login - Starting login process:', { email, isAdmin });
    dispatch(loginStart());
    try {
      const response = await loginService({ email, password }, isAdmin);
      console.log('useAuth login - Login service response:', response);

      if (isTwoFactorChallenge(response)) {
        const mode = response.requiresTwoFactorSetup ? 'setup' : 'verify';
        navigate(`/two-factor?mode=${mode}${isAdmin ? '&admin=true' : ''}`);
        return;
      }

      if (!response.token) {
        throw new Error('No token received');
      }

      dispatch(loginSuccess({ user: response.user!, token: response.token, agency: response.agency }));

      setTimeout(() => {
        if (isAdmin) {
          navigate('/admin/dashboard');
        } else if (response.agency?.slug) {
          navigate(`/agencies/${response.agency.slug}/dashboard`);
        } else {
          navigate('/dashboard');
        }
      }, 100);
    } catch (error: any) {
      console.error('useAuth login - Login failed:', error);
      dispatch(loginFailure(error.message || 'Login failed'));
      throw error;
    }
  };

  const registerAgency = useCallback(async (data: {
    name: string;
    email: string;
    website: string;
    phone: string;
    address: string;
    state: string;
    adminName: string;
    adminEmail: string;
    adminPhone: string;
  }) => {
    try {
      dispatch(loginStart());
      const response = await registerAgencyService(data);
      dispatch(loginSuccess(response));
      navigate('/login');
    } catch (error: any) {
      dispatch(loginFailure(error.response?.data?.message || 'Registration failed'));
    }
  }, [dispatch, navigate]);

  const logoutUser = () => {
    console.log('useAuth logoutUser - Starting logout process');
    // Reset the auth check flag so it can run again if needed
    hasCheckedAuth.current = false;
    
    // Determine the redirect URL based on user type
    let redirectUrl = '/login';
    if (window.location.pathname.startsWith('/admin')) {
      redirectUrl = '/admin/login';
    } else if (agency?.subscriptionPlan && /individual/i.test(String(agency.subscriptionPlan))) {
      redirectUrl = '/individual/login';
    } else if (agency?.slug) {
      redirectUrl = `/${agency.slug}`;
    }
    
    // Clear the Redux store
    dispatch(logout());
    // Force a page reload to ensure clean state
    window.location.href = redirectUrl;
  };

  const refreshAuth = useCallback(async () => {
    try {
      const storedToken = StorageManager.getToken();
      if (!storedToken) {
        console.log('No token found for refresh');
        return;
      }

      const isAdmin = window.location.pathname.startsWith('/admin');
      const response = await getProfileService(isAdmin);
      
      if (response.user) {
        dispatch(loginSuccess({ user: response.user, token: storedToken, agency: response.agency }));
        console.log('Auth refreshed successfully');
      }
    } catch (error) {
      console.error('Failed to refresh auth:', error);
    }
  }, [dispatch]);

  const deleteAccount = useCallback(async () => {
    await deleteAccountService();
    // After successful deletion, logout and redirect
    logoutUser();
  }, []);

  return {
    user,
    token,
    isAuthenticated,
    loading,
    error,
    login,
    registerAgency,
    logout: logoutUser,
    agency,
    // Expose updater so pages like Profile can refresh header avatar without reload
    updateUser: (u: any) => dispatch(updateUserAction(u)),
    // Expose refresh function to update auth state
    refreshAuth,
    deleteAccount,
  };
}; 